import type { BalanceData, AprData, LenderConfigMap, PostTradeMetrics } from '../utils'
import { getAssetConfig, computePostTradeMetrics } from '../utils'
import type { LenderYields } from '../../../../types/lenderTypes'
import { getOrganicYieldBorrow, getRewardYieldBorrow, getIntrinsicYieldBorrow } from '../yield'

/**
 * Compute the post-trade balance metrics for a borrow operation.
 *
 * A borrow increases debt, adjustedDebt, and decreases nav.
 * When `apr` and `yieldParams` are provided, also computes `aprDataPost`.
 */
export function computeBorrowDelta(
  amount: number,
  price: number,
  balanceData: BalanceData,
  config: LenderConfigMap,
  modeId: string,
  apr?: AprData,
  yieldParams?: LenderYields,
  irMode?: number,
): PostTradeMetrics {
  const { borrowFactor } = getAssetConfig(config, modeId)

  const dollarAmount = amount * price
  const postTrade: BalanceData = {
    ...balanceData,
    debt: balanceData.debt + dollarAmount,
    adjustedDebt: balanceData.adjustedDebt + borrowFactor * dollarAmount,
    nav: balanceData.nav - dollarAmount,
  }

  const result = computePostTradeMetrics(balanceData, postTrade)

  if (apr && yieldParams) {
    const mode = irMode ?? 2
    const cashFlowDeposits = balanceData.deposits * apr.depositApr
    const cashFlowDebt = balanceData.debt * apr.borrowApr
    const rewardFlowDeposits = balanceData.deposits * (apr.rewardDepositApr ?? 0)
    const rewardFlowDebt = balanceData.debt * (apr.rewardBorrowApr ?? 0)
    const intrinsicFlowDeposits = balanceData.deposits * apr.intrinsicDepositApr
    const intrinsicFlowDebt = balanceData.debt * apr.intrinsicBorrowApr

    const newApr = { ...apr }
    ;[newApr.borrowApr, newApr.depositApr, newApr.apr] = getOrganicYieldBorrow(
      dollarAmount, mode, cashFlowDeposits, cashFlowDebt, balanceData.deposits, balanceData.debt, yieldParams
    )
    newApr.rewards = getRewardYieldBorrow(
      dollarAmount, mode, rewardFlowDeposits, rewardFlowDebt, balanceData.deposits, balanceData.debt, yieldParams
    )
    ;[newApr.intrinsicBorrowApr, newApr.intrinsicDepositApr, newApr.intrinsicApr] =
      getIntrinsicYieldBorrow(
        dollarAmount, intrinsicFlowDeposits, intrinsicFlowDebt, balanceData.deposits, balanceData.debt, yieldParams
      )
    result.post.aprData = newApr
  }

  return result
}
